import { Box, Flex, Radio, Stack, Text } from '@mantine/core';
import { IconCheck, IconFileText, IconCalendar, IconClipboardText } from '@tabler/icons-react';
import { StepProps } from 'src/UI/drawer-flow/DrawerFlow';
import DrawerFlowScreenLayout from 'src/UI/drawer-flow/DrawerFlowScreenLayout';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { TemplateCreationData, TemplateType } from 'src/company/templates/types';

const templateTypeOptions = [
  {
    value: 'MEETING_NOTE' as const,
    title: 'Meeting notes',
    description: 'Templates for capturing meeting discussions and action items',
    icon: IconFileText,
  },
  {
    value: 'MEETING_AGENDA' as const,
    title: 'Meeting agenda',
    description: 'Structured templates for planning meeting topics and timing',
    icon: IconCalendar,
  },
  {
    value: 'SUITABILITY_REPORT' as const,
    title: 'Suitability report',
    description: 'Professional templates for assessment and evaluation reports',
    icon: IconClipboardText,
  },
];

export default function SelectTemplateTypeStep(props: StepProps<TemplateCreationData>) {
  const { data, setData } = props;

  const handleTemplateTypeChange = (value: string) => {
    setData({
      ...data,
      templateType: value as TemplateType,
    });
  };

  return (
    <DrawerFlowScreenLayout
      {...props}
      title="Select template type"
      description="Choose the type of template you want to create. This helps us optimise the processing for your specific use case."
      continueText="Continue"
      canGoForward={!!data.templateType}
      hideProgress={true}
    >
      <Radio.Group value={data.templateType} onChange={handleTemplateTypeChange}>
        <Stack gap="xs">
          {templateTypeOptions.map(option => {
            const IconComponent = option.icon;
            return (
              <Radio.Card
                key={option.value}
                value={option.value}
                radius="md"
                style={{
                  borderRadius: '16px',
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  borderColor:
                    data.templateType === option.value
                      ? 'var(--mantine-color-brand-6)'
                      : 'var(--mantine-color-gray-4)',
                  backgroundColor:
                    data.templateType === option.value
                      ? 'var(--mantine-color-brand-0)'
                      : 'transparent',
                }}
              >
                <Box p="sm">
                  <Flex align="center">
                    <IconComponent
                      size={20}
                      color={
                        data.templateType === option.value
                          ? 'var(--mantine-color-brand-6)'
                          : 'var(--mantine-color-gray-6)'
                      }
                      style={{ marginRight: '12px', flexShrink: 0 }}
                    />
                    <Stack gap="xxs" style={{ flex: 1, marginRight: '28px' }}>
                      <Text fw={FONT_WEIGHTS.bold} size="sm" c="gray.8">
                        {option.title}
                      </Text>
                      <Text fw={FONT_WEIGHTS.regular} size="xs" c="gray.8">
                        {option.description}
                      </Text>
                    </Stack>
                    <Box style={{ width: '20px', height: '20px' }}>
                      {data.templateType === option.value && (
                        <IconCheck size={20} color="var(--mantine-color-brand-6)" />
                      )}
                    </Box>
                  </Flex>
                </Box>
              </Radio.Card>
            );
          })}
        </Stack>
      </Radio.Group>
    </DrawerFlowScreenLayout>
  );
}
